import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useBooking } from "../../../context/BookingContext";
import { getAllStringsFromInventory } from "../../../api/api";
import styles from "./BookingForm.module.css";
import useAdminGuard from "../../../hooks/useAuthGuard";

const BookingForm = () => {
  useAdminGuard();
  const { pickupService: contextPickupService, addBooking } = useBooking();
  const [validationError, setValidationError] = useState("");

  const location = useLocation();
  const navigate = useNavigate();

  const {
    customerId,
    customerName = "N/A",
    freeServices = 0,
    usedServices = 0,
    freeServicesTotal = 0,
    isFree = false,
    racketName: prefilledRacketName,

    invoiceNo,
    instoreLocation: passedInstoreLocation = null,
    rp_id = null,
    claim_id = null,
    stringTypeConstraint = null,
    isAddMore = false,
    lockServiceSelection = false,
    pickupService: passedPickupService = null,
  } = location.state || {};

  const effectiveConstraint = stringTypeConstraint;

  // Service selection states
  const [pickupService, setPickupService] = useState(
    passedPickupService || contextPickupService || ""
  );
  const [instoreLocation, setInstoreLocation] = useState(
    passedInstoreLocation || ""
  );
  const [serviceSpeed, setServiceSpeed] = useState(effectiveConstraint || ""); // Regular or Express

  const [racketName, setRacketName] = useState(prefilledRacketName || "");
  const [selectedStringId, setSelectedStringId] = useState(null);
  const [selectedStringType, setSelectedStringType] = useState("");
  const [bookingDate, setBookingDate] = useState(
    location.state?.bookingDate || (() => {
      const now = new Date();
      return now.toISOString().split("T")[0];
    })()
  );
  const [totalCost, setTotalCost] = useState("");
  const [strings, setStrings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [apiError, setApiError] = useState("");

  const [isHybrid, setIsHybrid] = useState(false);
  const [hybridTab, setHybridTab] = useState("main"); // 'main' | 'cross'
  const [selectedStringIdCross, setSelectedStringIdCross] = useState(null);
  const [prestretchEnabledCross, setPrestretchEnabledCross] = useState(false);
  const [prestretchPercentCross, setPrestretchPercentCross] = useState("");
  const [showHybridInfo, setShowHybridInfo] = useState(false);
  const hybridInfoRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (hybridInfoRef.current && !hybridInfoRef.current.contains(event.target)) {
        setShowHybridInfo(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, []);

  // String tension state
  const [isTensionModalOpen, setIsTensionModalOpen] = useState(false);
  const [tensionModalTarget, setTensionModalTarget] = useState("all"); // 'all' | 'main' | 'cross'
  const [tensionOption, setTensionOption] = useState(""); // '' | 'standard' | 'custom' | 'support'
  const [tensionOptionMain, setTensionOptionMain] = useState(""); // '' | 'standard' | 'custom' | 'support'
  const [tensionOptionCross, setTensionOptionCross] = useState(""); // '' | 'standard' | 'custom' | 'support'
  const [tensionMain, setTensionMain] = useState("");
  const [tensionCross, setTensionCross] = useState("");
  const [tensionUnit, setTensionUnit] = useState("lbs");
  const [prestretchEnabled, setPrestretchEnabled] = useState(false);
  const [prestretchPercent, setPrestretchPercent] = useState("");

  // Fetch strings from inventory on component mount
  useEffect(() => {
    const fetchStrings = async () => {
      try {
        setLoading(true);
        const data = await getAllStringsFromInventory();
        setStrings(data);
        setApiError("");
      } catch (error) {
        console.error("Error fetching strings:", error);
        setApiError("Failed to load string inventory. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchStrings();
  }, []);

  useEffect(() => {
    if (selectedStringId && selectedStringType) {
      const stringDetails = strings.find(
        (item) => item.string_id === selectedStringId
      );
      if (stringDetails) {
        if (isHybrid) {
          const stringDetailsCross = selectedStringIdCross ? strings.find(
            (item) => item.string_id === selectedStringIdCross
          ) : null;
          const isMainByos = stringDetails.name === "Bring Own String" || (stringDetails.name || "").toLowerCase().includes("bring own");
          const isCrossByos = stringDetailsCross ? (stringDetailsCross.name === "Bring Own String" || (stringDetailsCross.name || "").toLowerCase().includes("bring own")) : false;
          const bothByos = isMainByos && isCrossByos;
          const isExpress = selectedStringType === "Express";

          let cost;
          if (bothByos) {
            if (isFree) {
              cost = isExpress ? 25 : 0;
            } else {
              cost = isExpress ? 100 : 75;
            }
          } else {
            if (isFree) {
              cost = isExpress ? 150 : 125;
            } else {
              cost = isExpress ? 225 : 200;
            }
          }
          setTotalCost(cost);
        } else {
          let cost;
          if (isFree) {
            cost =
              selectedStringType === "Express"
                ? Math.max(stringDetails.basePrice - 75 + 25, 0)
                : Math.max(stringDetails.basePrice - 75, 0);
          } else {
            cost =
              selectedStringType === "Express"
                ? stringDetails.basePrice + 25
                : stringDetails.basePrice;
          }
          setTotalCost(cost);
        }
      } else {
        setTotalCost("");
      }
    } else {
      setTotalCost("");
    }
  }, [selectedStringId, selectedStringIdCross, selectedStringType, isHybrid, isFree, strings]);

  const getCurrentDate = () => {
    const now = new Date();
    return now.toISOString().split("T")[0];
  };

  const handleNextClick = () => {
    if (!bookingDate.trim()) {
      setValidationError("Please select a booking date.");
      return;
    }
    if (!serviceSpeed) {
      setValidationError(
        "Please select when you want your racket ready (Regular or Express)."
      );
      return;
    }
    if (!pickupService) {
      setValidationError("Please select a pickup/delivery service.");
      return;
    }
    if (pickupService === "in-store" && !instoreLocation) {
      setValidationError("Please select a store location.");
      return;
    }
    if (!selectedStringId) {
      setHybridTab("main");
      setValidationError("Please select a main string before proceeding.");
      return;
    }

    if (isHybrid && !selectedStringIdCross) {
      setHybridTab("cross");
      setValidationError("Please select a cross string before proceeding.");
      return;
    }

    // Validate selected string is available at chosen location
    if (pickupService === "in-store" && instoreLocation) {
      const selectedString = strings.find(s => s.string_id === selectedStringId);
      if (selectedString) {
        let stockAtLocation = 0;
        const location = instoreLocation.toLowerCase().replace(/\s+/g, "");
        
        if (location === "jumeriah" || location === "jumeirah") {
          stockAtLocation = selectedString.jumeriah_stock || 0;
        } else if (location === "dubaihills") {
          stockAtLocation = selectedString.dubaihills_stock || 0;
        } else if (location === "marina") {
          stockAtLocation = Math.max(
            selectedString.jumeriah_stock || 0,
            selectedString.dubaihills_stock || 0
          );
        }
        
        if (stockAtLocation <= 0) {
          setValidationError(
            `Selected main string "${selectedString.name}" is out of stock at ${instoreLocation}. Please select a different string or change location.`
          );
          return;
        }
      }

      if (isHybrid && selectedStringIdCross) {
        const selectedCross = strings.find(s => s.string_id === selectedStringIdCross);
        if (selectedCross) {
          let stockAtLocation = 0;
          const location = instoreLocation.toLowerCase().replace(/\s+/g, "");
          
          if (location === "jumeriah" || location === "jumeirah") {
            stockAtLocation = selectedCross.jumeriah_stock || 0;
          } else if (location === "dubaihills") {
            stockAtLocation = selectedCross.dubaihills_stock || 0;
          } else if (location === "marina") {
            stockAtLocation = Math.max(
              selectedCross.jumeriah_stock || 0,
              selectedCross.dubaihills_stock || 0
            );
          }
          
          if (stockAtLocation <= 0) {
            setValidationError(
              `Selected cross string "${selectedCross.name}" is out of stock at ${instoreLocation}. Please select a different string or change location.`
            );
            return;
          }
        }
      }
    }
    if (isHybrid) {
      if (!tensionOptionMain) {
        setValidationError("Please select a tension option for the main string.");
        return;
      }
      if (!tensionOptionCross) {
        setValidationError("Please select a tension option for the cross string.");
        return;
      }
      if (tensionOptionMain === "custom") {
        if (!tensionMain || parseFloat(tensionMain) <= 0) {
          setValidationError("Please enter a valid main tension value.");
          return;
        }
        if (prestretchEnabled && (!prestretchPercent || parseFloat(prestretchPercent) <= 0)) {
          setValidationError("Please enter a valid main prestretch percentage.");
          return;
        }
      }
      if (tensionOptionCross === "custom") {
        if (!tensionCross || parseFloat(tensionCross) <= 0) {
          setValidationError("Please enter a valid cross tension value.");
          return;
        }
        if (prestretchEnabledCross && (!prestretchPercentCross || parseFloat(prestretchPercentCross) <= 0)) {
          setValidationError("Please enter a valid cross prestretch percentage.");
          return;
        }
      }
    } else {
      if (!tensionOption) {
        setValidationError("Please select a string tension option.");
        return;
      }
      if (tensionOption === "custom") {
        if (!tensionMain || parseFloat(tensionMain) <= 0) {
          setValidationError("Please enter a valid main tension value.");
          return;
        }
        if (!tensionCross || parseFloat(tensionCross) <= 0) {
          setValidationError("Please enter a valid cross tension value.");
          return;
        }
        if (prestretchEnabled && (!prestretchPercent || parseFloat(prestretchPercent) <= 0)) {
          setValidationError("Please enter a valid prestretch percentage.");
          return;
        }
      }
    }

    if (!isFree && !prefilledRacketName && !racketName.trim()) {
      setValidationError("Please enter a racket name.");
      return;
    }

    setValidationError("");

    const effectiveTensionType = isHybrid
      ? (tensionOptionMain === "standard" && tensionOptionCross === "standard"
          ? "standard"
          : (tensionOptionMain === "support" && tensionOptionCross === "support"
              ? "support"
              : "custom"))
      : tensionOption;

    addBooking({
      customerId,
      invoiceNo,
      racketName,
      bookingDate,
      stringId: selectedStringId,
      stringType: serviceSpeed,
      freeService: isFree,
      rp_id,
      claim_id,
      tensionType: effectiveTensionType,
      tensionOptionMain: isHybrid ? tensionOptionMain : null,
      tensionOptionCross: isHybrid ? tensionOptionCross : null,
      tensionMain: (isHybrid ? tensionOptionMain === "custom" : tensionOption === "custom") ? parseFloat(tensionMain) : null,
      tensionCross: (isHybrid ? tensionOptionCross === "custom" : tensionOption === "custom") ? parseFloat(tensionCross) : null,
      tensionUnit: (isHybrid ? (tensionOptionMain === "custom" || tensionOptionCross === "custom") : tensionOption === "custom") ? tensionUnit : null,
      prestretch: (isHybrid ? tensionOptionMain === "custom" : tensionOption === "custom") && prestretchEnabled ? 1 : 0,
      prestretchPercent:
        (isHybrid ? tensionOptionMain === "custom" : tensionOption === "custom") && prestretchEnabled && prestretchPercent
          ? parseFloat(prestretchPercent)
          : null,
      isHybrid: isHybrid ? 1 : 0,
      stringIdCross: isHybrid ? selectedStringIdCross : null,
      prestretchCross: isHybrid && tensionOptionCross === "custom" && prestretchEnabledCross ? 1 : 0,
      prestretchPercentCross:
        isHybrid && tensionOptionCross === "custom" && prestretchEnabledCross && prestretchPercentCross
          ? parseFloat(prestretchPercentCross)
          : null,
    });

    navigate("/dashboard/booking-final", {
      state: {
        customerId,
        customerName,
        bookingDate,
        freeServices,
        usedServices,
        freeServicesTotal,
        pickupService,
        services: "Stringing Service",
        string_id: selectedStringId,
        string_type: serviceSpeed,
        instoreLocation,
        rp_id,
        claim_id,
        racketName,
        isFreeService: isFree,
      },
    });
  };

  const renderStringCards = (isCross = false) => {
    const sortedStrings = strings.slice().sort((a, b) => {
      let aAvailable = false;
      let bAvailable = false;

      if (pickupService === "in-store") {
        if (instoreLocation === "Jumeriah") {
          aAvailable = a.jumeriah_stock > 0;
          bAvailable = b.jumeriah_stock > 0;
        } else if (instoreLocation === "Dubai Hills") {
          aAvailable = a.dubaihills_stock > 0;
          bAvailable = b.dubaihills_stock > 0;
        } else if (instoreLocation === "Marina") {
          aAvailable = a.availableInAnyStore;
          bAvailable = b.availableInAnyStore;
        }
      } else {
        aAvailable = a.availableInAnyStore;
        bAvailable = b.availableInAnyStore;
      }

      if (aAvailable !== bAvailable) return aAvailable ? -1 : 1;
      return (a.name || "").localeCompare(b.name || "");
    });

    if (sortedStrings.length === 0) {
      return <p className={styles.noStrings}>No strings in inventory.</p>;
    }

    return sortedStrings.map((stringItem) => {
      const originalPrice =
        serviceSpeed === "Express"
          ? stringItem.basePrice + 25
          : stringItem.basePrice;

      const priceForRow = isFree ? Math.max(originalPrice - 75, 0) : originalPrice;

      const isSelected = isCross
        ? selectedStringIdCross === stringItem.string_id
        : selectedStringId === stringItem.string_id;
      const isBYOS = stringItem.name === "Bring Own String" || (stringItem.name || "").toLowerCase().includes("bring own");

      let isAvailableAtSelectedLocation = false;
      let availableAtOtherStore = null;
      let isOutOfStockEverywhere = false;

      if (pickupService === "in-store") {
        if (instoreLocation === "Jumeriah") {
          isAvailableAtSelectedLocation = stringItem.jumeriah_stock > 0;
          if (!isAvailableAtSelectedLocation && stringItem.dubaihills_stock > 0) {
            availableAtOtherStore = "Dubai Hills";
          }
        } else if (instoreLocation === "Dubai Hills") {
          isAvailableAtSelectedLocation = stringItem.dubaihills_stock > 0;
          if (!isAvailableAtSelectedLocation && stringItem.jumeriah_stock > 0) {
            availableAtOtherStore = "Jumeriah";
          }
        } else if (instoreLocation === "Marina") {
          isAvailableAtSelectedLocation = stringItem.availableInAnyStore;
        }
      } else {
        isAvailableAtSelectedLocation = stringItem.availableInAnyStore;
      }

      isOutOfStockEverywhere = !stringItem.jumeriah_stock && !stringItem.dubaihills_stock;

      const cdnBase = process.env.REACT_APP_CDN_URL || "https://cdn11.bigcommerce.com/s-meuoh09dlj";
      const imageUrl = stringItem.image_url
        ? (stringItem.image_url.startsWith("http") ? stringItem.image_url : `${cdnBase}${stringItem.image_url}`)
        : "/images/strings/default.jpg";

      return (
        <div
          key={stringItem.string_id}
          className={`${styles.card} ${isSelected ? styles.cardSelected : ""} ${
            !isAvailableAtSelectedLocation ? styles.cardDisabled : ""
          }`}
        >
          <label className={styles.cardTop}>
            <input
              type="radio"
              name={isCross ? "selectedStringCross" : "selectedStringMain"}
              value={stringItem.string_id || ""}
              onChange={() => {
                if (!isAvailableAtSelectedLocation) return;
                if (isCross) {
                  setSelectedStringIdCross(stringItem.string_id);
                  if (isHybrid) {
                    setTensionModalTarget("cross");
                  } else {
                    setTensionModalTarget("all");
                  }
                  setIsTensionModalOpen(true);
                } else {
                  setSelectedStringId(stringItem.string_id);
                  setSelectedStringType(serviceSpeed);
                  if (isHybrid) {
                    setTensionModalTarget("main");
                  } else {
                    setTensionModalTarget("all");
                  }
                  setIsTensionModalOpen(true);
                  if (isHybrid && !selectedStringIdCross) {
                    setHybridTab("cross");
                  }
                }
              }}
              checked={isSelected}
              disabled={!isAvailableAtSelectedLocation}
              className={styles.cardSelector}
              aria-label={`Select ${stringItem.name}`}
            />

            <div className={styles.imageWrap}>
              <img
                className={styles.cardImage}
                src={imageUrl}
                alt={stringItem.name}
                loading="lazy"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = "/images/strings/default.jpg";
                }}
              />
              <div className={styles.imageBadges}>
                {isOutOfStockEverywhere && <span className={styles.oosBadge}>Out of Stock</span>}
                {!isOutOfStockEverywhere && !isAvailableAtSelectedLocation && availableAtOtherStore && (
                  <span className={styles.switchStoreBadge}>Available at {availableAtOtherStore}</span>
                )}
              </div>
            </div>
          </label>

          <div className={styles.cardBody}>
            <div className={styles.cardTitle} title={`${stringItem.name} (${stringItem.brand})`}>
              {stringItem.name} ({stringItem.brand})
            </div>

            {!isOutOfStockEverywhere && !isAvailableAtSelectedLocation && availableAtOtherStore && (
              <div className={styles.switchStoreMessage}>
                Want to switch to {availableAtOtherStore} store?
              </div>
            )}

            <div className={styles.cardInfo}>
              {isHybrid ? (
                <span className={styles.badge}>{isBYOS ? "Own String" : "Half String (Hybrid)"}</span>
              ) : (
                <>
                  <span className={styles.badge}>{serviceSpeed}</span>
                  <span className={styles.cardPrice}>
                    {isFree ? (
                      <>
                        <span style={{ textDecoration: "line-through", marginRight: "8px", opacity: 0.6 }}>
                          AED {originalPrice}
                        </span>
                        AED {priceForRow}
                      </>
                    ) : (
                      <>
                        AED {priceForRow}
                      </>
                    )}
                  </span>
                </>
              )}
            </div>
          </div>
        </div>
      );
    });
  };

  return (
    <div className={styles.bookingForm}>
      <h2 className={styles.title}>Create Booking</h2>

      <div className={styles.detailsContainer}>
        {validationError && (
          <p className={styles.validationError} role="alert">
            {validationError}
          </p>
        )}
        <div className={styles.detailRow}>
          <div className={styles.detailItem}>
            <label className={styles.label}>Customer Name:</label>
            <span className={styles.disabledText}>{customerName}</span>
          </div>
          <div className={styles.detailItem}>
            <label className={styles.label}>Services:</label>
            <span className={styles.disabledText}>Stringing Service</span>
          </div>
        </div>

        <div className={styles.detailRow}>
          <div className={styles.detailItem}>
            <label className={styles.label} htmlFor="bf-date">
              Booking Date:
            </label>
            <input
              id="bf-date"
              type="date"
              value={bookingDate}
              onChange={(e) => setBookingDate(e.target.value)}
              className={styles.dateInput}
              min={getCurrentDate()}
            />
          </div>
        </div>

        <div className={styles.formGroup}>
          <label className={styles.label} htmlFor="bf-racket">
            Racket Name:
          </label>
          <input
            id="bf-racket"
            type="text"
            className={styles.inputField}
            placeholder="Enter racket name"
            value={racketName}
            onChange={(e) => setRacketName(e.target.value)}
            disabled={isFree || !!rp_id}
          />
        </div>

        {/* Service Speed Selection */}
        <div className={styles.formGroup}>
          <label className={styles.label}>
            When will your racket be ready?
          </label>
          <div className={styles.serviceSpeedOptions}>
            <label className={styles.serviceSpeedOption}>
              <input
                type="radio"
                name="serviceSpeed"
                value="Regular"
                checked={serviceSpeed === "Regular"}
                onChange={(e) => {
                  setServiceSpeed(e.target.value);
                  setSelectedStringType("Regular");
                  setPickupService(""); // Reset pickup service when speed changes
                }}
                disabled={
                  effectiveConstraint && effectiveConstraint !== "Regular"
                }
              />
              <div className={styles.serviceSpeedContent}>
                <strong>Regular: Next day</strong>
                <span className={styles.serviceSpeedDescription}>
                  String installation ready by next day
                </span>
              </div>
            </label>
            <label className={styles.serviceSpeedOption}>
              <input
                type="radio"
                name="serviceSpeed"
                value="Express"
                checked={serviceSpeed === "Express"}
                onChange={(e) => {
                  setServiceSpeed(e.target.value);
                  setSelectedStringType("Express");
                  setPickupService(""); // Reset pickup service when speed changes
                }}
                disabled={
                  effectiveConstraint && effectiveConstraint !== "Express"
                }
              />
              <div className={styles.serviceSpeedContent}>
                <strong>Express: Within 6 hours</strong>
                <span className={styles.serviceSpeedDescription}>
                  Fast-track service for urgent needs
                </span>
              </div>
            </label>
          </div>
        </div>

        {/* Pickup/Delivery Service Selection */}
        {serviceSpeed && (
          <div className={styles.formGroup}>
            <label className={styles.label}>
              Select Pickup/Delivery Service:
            </label>
            <div className={styles.pickupServiceOptions}>
              <label className={styles.pickupOption}>
                <input
                  type="radio"
                  name="pickupService"
                  value="in-store"
                  checked={pickupService === "in-store"}
                  onChange={(e) => setPickupService(e.target.value)}
                  disabled={lockServiceSelection}
                />
                <span>In-Store (No Charge)</span>
              </label>
              {serviceSpeed === "Regular" ? (
                <>
                  <label className={styles.pickupOption}>
                    <input
                      type="radio"
                      name="pickupService"
                      value="pickup"
                      checked={pickupService === "pickup"}
                      onChange={(e) => setPickupService(e.target.value)}
                      disabled={lockServiceSelection}
                    />
                    <span>
                      Pickup Only (+{" "}
                      <span className="dirham-symbol">&#xea;</span> 30)
                    </span>
                  </label>
                  <label className={styles.pickupOption}>
                    <input
                      type="radio"
                      name="pickupService"
                      value="delivery"
                      checked={pickupService === "delivery"}
                      onChange={(e) => setPickupService(e.target.value)}
                      disabled={lockServiceSelection}
                    />
                    <span>
                      Delivery Only (+{" "}
                      <span className="dirham-symbol">&#xea;</span> 30)
                    </span>
                  </label>
                  <label className={styles.pickupOption}>
                    <input
                      type="radio"
                      name="pickupService"
                      value="pickup/delivery"
                      checked={pickupService === "pickup/delivery"}
                      onChange={(e) => setPickupService(e.target.value)}
                      disabled={lockServiceSelection}
                    />
                    <span>
                      Both Pickup & Delivery (+{" "}
                      <span className="dirham-symbol">&#xea;</span> 60)
                    </span>
                  </label>
                </>
              ) : (
                <>
                  <label className={styles.pickupOption}>
                    <input
                      type="radio"
                      name="pickupService"
                      value="express-pickup"
                      checked={pickupService === "express-pickup"}
                      onChange={(e) => setPickupService(e.target.value)}
                      disabled={lockServiceSelection}
                    />
                    <span>
                      Express Pickup (+{" "}
                      <span className="dirham-symbol">&#xea;</span> 50)
                    </span>
                  </label>
                  <label className={styles.pickupOption}>
                    <input
                      type="radio"
                      name="pickupService"
                      value="express-delivery"
                      checked={pickupService === "express-delivery"}
                      onChange={(e) => setPickupService(e.target.value)}
                      disabled={lockServiceSelection}
                    />
                    <span>
                      Express Delivery (+{" "}
                      <span className="dirham-symbol">&#xea;</span> 50)
                    </span>
                  </label>
                  <label className={styles.pickupOption}>
                    <input
                      type="radio"
                      name="pickupService"
                      value="express-pickup/delivery"
                      checked={pickupService === "express-pickup/delivery"}
                      onChange={(e) => setPickupService(e.target.value)}
                      disabled={lockServiceSelection}
                    />
                    <span>
                      Express Pickup & Delivery (+{" "}
                      <span className="dirham-symbol">&#xea;</span> 100)
                    </span>
                  </label>
                </>
              )}
            </div>
          </div>
        )}

        {/* Store Location Selection for In-Store */}
        {pickupService === "in-store" && (
          <div className={styles.formGroup}>
            <label className={styles.label} htmlFor="bf-location">
              Select Store Location:
            </label>
            <select
              id="bf-location"
              className={styles.inputField}
              value={instoreLocation}
              onChange={(e) => {
                const newLocation = e.target.value;
                setInstoreLocation(newLocation);

                // Clear selected string when location changes
                // User must re-select string for the new location
                setSelectedStringId(null);
                setSelectedStringIdCross(null);
                setSelectedStringType("");
                setTensionOption("");
                setTensionOptionMain("");
                setTensionOptionCross("");
                setTensionMain("");
                setTensionCross("");
                setPrestretchEnabled(false);
                setPrestretchPercent("");
                setPrestretchEnabledCross(false);
                setPrestretchPercentCross("");
              }}
              disabled={lockServiceSelection}
            >
              <option value="">-- Select Location --</option>
              <option value="Jumeriah">Jumeriah</option>
              <option value="Dubai Hills">Dubai Hills</option>
              <option value="Marina">Marina (Any Available Location)</option>
            </select>
            {instoreLocation === "Marina" && (
              <span style={{ fontSize: "0.825rem", color: "#c2410c", marginTop: "6px", display: "block" }}>
                ℹ️ Note: Marina Store does not have an on-site Express technician (Express bookings dropped at Marina require transfer to Jumeirah).
              </span>
            )}
          </div>
        )}
      </div>

      {pickupService && (pickupService === "in-store" ? instoreLocation : true) && (
        <div className={styles.formGroup} style={{ margin: "20px 24px" }}>
          <label className={styles.label}>Stringing Option:</label>
          <div className={styles.radioGroup}>
            <label className={styles.pickupOption}>
              <input
                type="radio"
                name="hybridOption"
                checked={!isHybrid}
                onChange={() => {
                  setIsHybrid(false);
                  setSelectedStringIdCross(null);
                  setPrestretchEnabledCross(false);
                  setPrestretchPercentCross("");
                  setHybridTab("main");
                }}
              />
              <span>Single String Setup</span>
            </label>
            <label className={styles.pickupOption}>
              <input
                type="radio"
                name="hybridOption"
                checked={isHybrid}
                onChange={() => {
                  setIsHybrid(true);
                  setHybridTab("main");
                }}
              />
              <span className={styles.hybridOptionLabel}>
                <span>Hybrid String Setup (Main & Cross)</span>
                <span
                  ref={hybridInfoRef}
                  className={styles.infoWrap}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                >
                  <button
                    type="button"
                    className={styles.infoBtn}
                    aria-label="Hybrid pricing information"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setShowHybridInfo((prev) => !prev);
                    }}
                  >
                    i
                  </button>
                  <div
                    className={`${styles.infoTooltip} ${
                      showHybridInfo ? styles.infoTooltipVisible : ""
                    }`}
                    role="tooltip"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {isFree ? (
                      <>
                        <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "6px", fontWeight: "700", color: "#60a5fa", fontSize: "0.95rem" }}>
                          <span>🎁</span> Free String Service Applied
                        </div>
                        <p style={{ margin: "0 0 6px 0", color: "#f3f4f6" }}>
                          For Hybrid Stringing, your <strong>75 AED</strong> service fee is covered!
                        </p>
                        <p style={{ margin: "0 0 6px 0", color: "#e5e7eb" }}>
                          Total cost is <strong>125 AED</strong> (200 - 75 AED) Regular / <strong>150 AED</strong> (225 - 75 AED) Express when selecting store strings.
                        </p>
                        <p style={{ margin: 0, fontSize: "0.82rem", color: "#9ca3af" }}>
                          (If bringing own strings for both main and cross: <strong>0 AED</strong> Regular / <strong>25 AED</strong> Express).
                        </p>
                      </>
                    ) : (
                      <>
                        <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "6px", fontWeight: "700", color: "var(--primary-color, #d2fa10)", fontSize: "0.95rem" }}>
                          <span>ℹ️</span> Consolidated Hybrid Pricing
                        </div>
                        <p style={{ margin: "0 0 6px 0", color: "#f3f4f6" }}>
                          Flat <strong>200 AED</strong> Regular / <strong>225 AED</strong> Express when selecting store strings. (If bringing own strings for both main and cross, service is <strong>75 AED</strong> Regular / <strong>100 AED</strong> Express).
                        </p>
                        <p style={{ margin: 0, fontSize: "0.82rem", color: "#9ca3af" }}>
                          *With Free String Service: 75 AED service fee is deducted &mdash; <strong>125 AED</strong> (200 - 75) Regular / <strong>150 AED</strong> (225 - 75) Express.
                        </p>
                      </>
                    )}
                  </div>
                </span>
              </span>
            </label>
          </div>
        </div>
      )}

      {/* String Selection - Only show when service is selected */}
      {pickupService &&
        (pickupService === "in-store" ? instoreLocation : true) && (
          <div className={styles.formGroup}>
            {isHybrid ? (
              <>
                {/* --- TWO TABS FOR HYBRID SELECTION --- */}
                {(() => {
                  const selectedMain = strings.find((s) => s.string_id === selectedStringId);
                  const selectedCross = strings.find((s) => s.string_id === selectedStringIdCross);
                  return (
                    <div style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: "12px",
                      marginBottom: "20px",
                      marginTop: "10px"
                    }}>
                      <button
                        type="button"
                        onClick={() => setHybridTab("main")}
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "flex-start",
                          padding: "14px 18px",
                          borderRadius: "10px",
                          border: hybridTab === "main" ? "2px solid #0b7b43" : "1px solid #d1d5db",
                          background: hybridTab === "main" ? "#f0fdf4" : "#ffffff",
                          cursor: "pointer",
                          textAlign: "left",
                          transition: "all 0.2s ease",
                          boxShadow: hybridTab === "main" ? "0 2px 8px rgba(11, 123, 67, 0.15)" : "none"
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", marginBottom: "4px" }}>
                          <span style={{ fontWeight: "700", fontSize: "1.05rem", color: hybridTab === "main" ? "#0b7b43" : "#374151" }}>
                            1. Main String
                          </span>
                          {selectedMain ? (
                            <span style={{ background: "#0b7b43", color: "#fff", fontSize: "0.75rem", padding: "2px 8px", borderRadius: "12px", fontWeight: "600" }}>
                              ✓ Selected
                            </span>
                          ) : (
                            <span style={{ background: "#e5e7eb", color: "#6b7280", fontSize: "0.75rem", padding: "2px 8px", borderRadius: "12px", fontWeight: "500" }}>
                              Required
                            </span>
                          )}
                        </div>
                        <span style={{ fontSize: "0.9rem", color: selectedMain ? "#111827" : "#9ca3af", fontWeight: selectedMain ? "600" : "400" }}>
                          {selectedMain ? `${selectedMain.brand} ${selectedMain.name}` : "Click to select main string"}
                        </span>
                        {selectedMain && (
                          <div style={{ marginTop: "6px", fontSize: "0.8rem", color: "#0b7b43", fontWeight: "600", display: "flex", alignItems: "center", gap: "6px" }}>
                            <span>
                              Tension: {tensionOptionMain === "custom"
                                ? `${tensionMain || "-"} ${tensionUnit}${prestretchEnabled ? ` (${prestretchPercent || "-"}% Prestretch)` : ""}`
                                : tensionOptionMain === "standard"
                                ? "Standard"
                                : tensionOptionMain === "support"
                                ? "Talk with Support"
                                : "Not set"}
                            </span>
                            <span
                              role="button"
                              tabIndex={0}
                              onClick={(e) => {
                                e.stopPropagation();
                                setTensionModalTarget("main");
                                setIsTensionModalOpen(true);
                              }}
                              style={{ textDecoration: "underline", cursor: "pointer", color: "#0b7b43", fontSize: "0.75rem" }}
                            >
                              (Edit)
                            </span>
                          </div>
                        )}
                      </button>

                      <button
                        type="button"
                        onClick={() => setHybridTab("cross")}
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "flex-start",
                          padding: "14px 18px",
                          borderRadius: "10px",
                          border: hybridTab === "cross" ? "2px solid #0b7b43" : "1px solid #d1d5db",
                          background: hybridTab === "cross" ? "#f0fdf4" : "#ffffff",
                          cursor: "pointer",
                          textAlign: "left",
                          transition: "all 0.2s ease",
                          boxShadow: hybridTab === "cross" ? "0 2px 8px rgba(11, 123, 67, 0.15)" : "none"
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", marginBottom: "4px" }}>
                          <span style={{ fontWeight: "700", fontSize: "1.05rem", color: hybridTab === "cross" ? "#0b7b43" : "#374151" }}>
                            2. Cross String
                          </span>
                          {selectedCross ? (
                            <span style={{ background: "#0b7b43", color: "#fff", fontSize: "0.75rem", padding: "2px 8px", borderRadius: "12px", fontWeight: "600" }}>
                              ✓ Selected
                            </span>
                          ) : (
                            <span style={{ background: "#fed7aa", color: "#c2410c", fontSize: "0.75rem", padding: "2px 8px", borderRadius: "12px", fontWeight: "600" }}>
                              Action Needed
                            </span>
                          )}
                        </div>
                        <span style={{ fontSize: "0.9rem", color: selectedCross ? "#111827" : "#9ca3af", fontWeight: selectedCross ? "600" : "400" }}>
                          {selectedCross ? `${selectedCross.brand} ${selectedCross.name}` : "Click to select cross string"}
                        </span>
                        {selectedCross && (
                          <div style={{ marginTop: "6px", fontSize: "0.8rem", color: "#0b7b43", fontWeight: "600", display: "flex", alignItems: "center", gap: "6px" }}>
                            <span>
                              Tension: {tensionOptionCross === "custom"
                                ? `${tensionCross || "-"} ${tensionUnit}${prestretchEnabledCross ? ` (${prestretchPercentCross || "-"}% Prestretch)` : ""}`
                                : tensionOptionCross === "standard"
                                ? "Standard"
                                : tensionOptionCross === "support"
                                ? "Talk with Support"
                                : "Not set"}
                            </span>
                            <span
                              role="button"
                              tabIndex={0}
                              onClick={(e) => {
                                e.stopPropagation();
                                setTensionModalTarget("cross");
                                setIsTensionModalOpen(true);
                              }}
                              style={{ textDecoration: "underline", cursor: "pointer", color: "#0b7b43", fontSize: "0.75rem" }}
                            >
                              (Edit)
                            </span>
                          </div>
                        )}
                      </button>
                    </div>
                  );
                })()}

                {/* --- ACTIVE TAB HEADING --- */}
                <div style={{ marginBottom: "20px" }}>
                  <label className={styles.label} style={{ fontSize: "1.2rem", color: "#0b7b43", marginBottom: "12px", display: "block" }}>
                    {hybridTab === "main" ? "1. Select Main String" : "2. Select Cross String"}
                    {pickupService === "in-store" && instoreLocation && (
                      <span className={styles.locationBadge}>
                        {" "}
                        (Available at {instoreLocation})
                      </span>
                    )}
                  </label>
                  
                  {loading && <p>Loading string inventory...</p>}
                  {apiError && <p className={styles.validationError}>{apiError}</p>}
                  
                  {!loading && !apiError && (
                    <div className={styles.cardsGrid}>
                      {renderStringCards(hybridTab === "cross")}
                    </div>
                  )}

                  {/* Switch to Cross tab helper if main is selected */}
                  {hybridTab === "main" && selectedStringId && !selectedStringIdCross && (
                    <div style={{ textAlign: "center", marginTop: "24px" }}>
                      <button
                        type="button"
                        onClick={() => setHybridTab("cross")}
                        style={{
                          background: "#0b7b43",
                          color: "#fff",
                          border: "none",
                          borderRadius: "8px",
                          padding: "12px 26px",
                          fontSize: "1rem",
                          fontWeight: "600",
                          cursor: "pointer",
                          boxShadow: "0 2px 8px rgba(0,0,0,0.12)"
                        }}
                      >
                        Next: Choose Cross String &rarr;
                      </button>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <>
                <label className={styles.label}>
                  Select Racket String
                  {pickupService === "in-store" && instoreLocation && (
                    <span className={styles.locationBadge}>
                      {" "}
                      (Available at {instoreLocation})
                    </span>
                  )}
                </label>

                {loading && <p>Loading string inventory...</p>}
                {apiError && <p className={styles.validationError}>{apiError}</p>}

                {!loading && !apiError && (
                  <div className={styles.cardsGrid}>
                    {renderStringCards(false)}
                  </div>
                )}
              </>
            )}
          </div>
        )}

      {/* Show buttons only when string is selected */}
      {selectedStringId && (
        <button
          type="button"
          onClick={() => {
            if (isHybrid) {
              if (!selectedStringIdCross) {
                setTensionModalTarget("main");
              } else {
                setTensionModalTarget("all");
              }
            } else {
              setTensionModalTarget("all");
            }
            setIsTensionModalOpen(true);
          }}
          className={styles.editTensionBtn}
        >
          <span>
            {isHybrid ? (
              (!tensionOptionMain && !tensionOptionCross)
                ? "Select String Tension (Required)"
                : `Tension: Main [${
                    tensionOptionMain === "custom"
                      ? `${tensionMain || "-"} ${tensionUnit}${prestretchEnabled ? ` (${prestretchPercent || "-"}% Prestretch)` : ""}`
                      : tensionOptionMain === "standard"
                      ? "Standard"
                      : tensionOptionMain === "support"
                      ? "Support"
                      : "Not set"
                  }] / Cross [${
                    tensionOptionCross === "custom"
                      ? `${tensionCross || "-"} ${tensionUnit}${prestretchEnabledCross ? ` (${prestretchPercentCross || "-"}% Prestretch)` : ""}`
                      : tensionOptionCross === "standard"
                      ? "Standard"
                      : tensionOptionCross === "support"
                      ? "Support"
                      : "Not set"
                  }]`
            ) : (
              tensionOption === "custom"
                ? `Tension: Custom (${tensionMain || '-'} ${tensionUnit} / ${tensionCross || '-'} ${tensionUnit})`
                : tensionOption === "standard"
                ? "Tension: Standard"
                : tensionOption === "support"
                ? "Tension: Talk with Support"
                : "Select String Tension (Required)"
            )}
          </span>
          <span style={{ fontSize: "1.2rem", lineHeight: "1" }}>&#x2192;</span>
        </button>
      )}

      {selectedStringId && (!isHybrid || selectedStringIdCross) && (
        <div style={{
          background: "#f8fafc",
          border: "1px solid #e2e8f0",
          borderRadius: "10px",
          padding: "12px 16px",
          marginBottom: "16px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "10px"
        }}>
          <div>
            <div style={{ fontWeight: "700", color: "#0f172a", fontSize: "0.95rem" }}>
              {isHybrid ? (
                <>Hybrid: {strings.find(s => s.string_id === selectedStringId)?.name || "Main"} + {strings.find(s => s.string_id === selectedStringIdCross)?.name || "Cross"}</>
              ) : (
                <>{strings.find(s => s.string_id === selectedStringId)?.name}</>
              )}
            </div>
            <div style={{ fontSize: "0.85rem", color: "#64748b" }}>
              Speed: <strong>{serviceSpeed}</strong> {isFree ? " • Free String Service Applied" : ""}
            </div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: "0.8rem", color: "#64748b" }}>Total Cost</div>
            <div style={{ fontSize: "1.15rem", fontWeight: "800", color: "#16a34a" }}>
              {isFree && (
                <span style={{ textDecoration: "line-through", color: "#94a3b8", fontSize: "0.9rem", marginRight: "6px" }}>
                  AED {isHybrid ? (serviceSpeed === "Express" ? 225 : 200) : (serviceSpeed === "Express" ? (strings.find(s => s.string_id === selectedStringId)?.basePrice + 25 || 0) : (strings.find(s => s.string_id === selectedStringId)?.basePrice || 0))}
                </span>
              )}
              AED {totalCost}
            </div>
          </div>
        </div>
      )}

      {selectedStringId && (
        <div className={styles.buttonContainer}>
          <button
            type="button"
            className={styles.cancelButton}
            onClick={() => {
              setSelectedStringId(null);
              setSelectedStringIdCross(null);
              setSelectedStringType("");
              setTotalCost("");
              setTensionOption("");
              setTensionOptionMain("");
              setTensionOptionCross("");
              setTensionMain("");
              setTensionCross("");
              setTensionUnit("lbs");
              setPrestretchEnabled(false);
              setPrestretchPercent("");
              setPrestretchEnabledCross(false);
              setPrestretchPercentCross("");
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            className={styles.nextButton}
            onClick={handleNextClick}
          >
            Next
          </button>
        </div>
      )}

      {isTensionModalOpen && (
        <div className={styles.modal} onClick={() => setIsTensionModalOpen(false)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3 className={styles.modalTitle}>
                {isHybrid
                  ? tensionModalTarget === "cross"
                    ? "Configure Cross String Tension"
                    : "Configure Main String Tension"
                  : "Configure String Tension"}
              </h3>
              <button 
                className={styles.modalCloseIcon} 
                onClick={() => setIsTensionModalOpen(false)}
                aria-label="Close"
              >
                &#10005;
              </button>
            </div>

            {/* Hybrid Sub-tabs */}
            {isHybrid && (
              <div style={{ display: "flex", gap: "8px", marginBottom: "16px" }}>
                <button
                  type="button"
                  style={{
                    flex: 1,
                    padding: "10px 12px",
                    borderRadius: "8px",
                    fontWeight: "700",
                    fontSize: "0.85rem",
                    cursor: "pointer",
                    border: tensionModalTarget !== "cross" ? "2px solid #0b7b43" : "1px solid #d1d5db",
                    background: tensionModalTarget !== "cross" ? "#f0fdf4" : "#ffffff",
                    color: tensionModalTarget !== "cross" ? "#0b7b43" : "#4b5563",
                    transition: "all 0.2s ease"
                  }}
                  onClick={() => setTensionModalTarget("main")}
                >
                  1. Main String {tensionOptionMain ? `(${tensionOptionMain === "custom" ? (tensionMain ? `${tensionMain}${tensionUnit}` : "Custom") : tensionOptionMain === "standard" ? "Standard" : "Support"})` : ""}
                </button>
                <button
                  type="button"
                  style={{
                    flex: 1,
                    padding: "10px 12px",
                    borderRadius: "8px",
                    fontWeight: "700",
                    fontSize: "0.85rem",
                    cursor: "pointer",
                    border: tensionModalTarget === "cross" ? "2px solid #0b7b43" : "1px solid #d1d5db",
                    background: tensionModalTarget === "cross" ? "#f0fdf4" : "#ffffff",
                    color: tensionModalTarget === "cross" ? "#0b7b43" : "#4b5563",
                    transition: "all 0.2s ease"
                  }}
                  onClick={() => setTensionModalTarget("cross")}
                >
                  2. Cross String {tensionOptionCross ? `(${tensionOptionCross === "custom" ? (tensionCross ? `${tensionCross}${tensionUnit}` : "Custom") : tensionOptionCross === "standard" ? "Standard" : "Support"})` : ""}
                </button>
              </div>
            )}
            
            {(() => {
              const activeTensionOption = isHybrid
                ? (tensionModalTarget === "cross" ? tensionOptionCross : tensionOptionMain)
                : tensionOption;

              const handleSelectTensionOption = (opt) => {
                if (isHybrid) {
                  if (tensionModalTarget === "cross") {
                    setTensionOptionCross(opt);
                  } else {
                    setTensionOptionMain(opt);
                  }
                } else {
                  setTensionOption(opt);
                }
              };

              return (
                <>
                  <div className={styles.formGroup}>
                    <label className={styles.label}>
                      Select Option {isHybrid ? `(${tensionModalTarget === "cross" ? "Cross String" : "Main String"})` : ""}
                    </label>
                    <div className={styles.tensionOptions}>
                      <button
                        type="button"
                        className={`${styles.tensionBtn} ${
                          activeTensionOption === "standard" ? styles.tensionBtnActive : ""
                        }`}
                        onClick={() => handleSelectTensionOption("standard")}
                      >
                        Standard
                      </button>
                      <button
                        type="button"
                        className={`${styles.tensionBtn} ${
                          activeTensionOption === "custom" ? styles.tensionBtnActive : ""
                        }`}
                        onClick={() => handleSelectTensionOption("custom")}
                      >
                        Custom
                      </button>
                      <button
                        type="button"
                        className={`${styles.tensionBtn} ${
                          activeTensionOption === "support" ? styles.tensionBtnActive : ""
                        }`}
                        onClick={() => handleSelectTensionOption("support")}
                      >
                        Talk with support
                      </button>
                    </div>
                  </div>



                  {activeTensionOption === "support" && (
                    <div className={styles.tensionSupport} style={{ marginTop: "16px" }}>
                      <p>
                        Our support team will call you to discuss the right tension {isHybrid ? `for ${tensionModalTarget === "cross" ? "Cross" : "Main"} String` : ""}.{" "}
                        <a href="tel:+971503469515" className={styles.tensionSupportLink}>
                          +971 503 469 515
                        </a>
                      </p>
                    </div>
                  )}

                  {activeTensionOption === "custom" && (
                    <div className={styles.tensionCustom}>
                      <div className={styles.unitToggle}>
                        <button
                          type="button"
                          className={`${styles.unitBtn} ${
                            tensionUnit === "lbs" ? styles.unitBtnActive : ""
                          }`}
                          onClick={() => setTensionUnit("lbs")}
                        >
                          lbs
                        </button>
                        <button
                          type="button"
                          className={`${styles.unitBtn} ${
                            tensionUnit === "kg" ? styles.unitBtnActive : ""
                          }`}
                          onClick={() => setTensionUnit("kg")}
                        >
                          kg
                        </button>
                      </div>

                      {isHybrid && tensionModalTarget === "cross" && (
                        <div style={{
                          background: "#f8fafc",
                          border: "1px solid #e2e8f0",
                          borderRadius: "8px",
                          padding: "8px 12px",
                          fontSize: "0.85rem",
                          color: "#475569",
                          marginBottom: "12px"
                        }}>
                          Main String Tension: <strong>{tensionOptionMain === "custom" ? `${tensionMain || "-"} ${tensionUnit}` : tensionOptionMain === "standard" ? "Standard" : tensionOptionMain === "support" ? "Support" : "Not configured yet"}</strong>
                        </div>
                      )}
                      {isHybrid && tensionModalTarget !== "cross" && (
                        <div style={{
                          background: "#f8fafc",
                          border: "1px solid #e2e8f0",
                          borderRadius: "8px",
                          padding: "8px 12px",
                          fontSize: "0.85rem",
                          color: "#475569",
                          marginBottom: "12px"
                        }}>
                          Cross String Tension: <strong>{tensionOptionCross === "custom" ? `${tensionCross || "-"} ${tensionUnit}` : tensionOptionCross === "standard" ? "Standard" : tensionOptionCross === "support" ? "Support" : "Not configured yet"}</strong>
                        </div>
                      )}

                      <div className={styles.tensionFields}>
                        {(!isHybrid || tensionModalTarget !== "cross") ? (
                          <div className={styles.tensionField}>
                            <label className={styles.tensionFieldLabel}>
                              {isHybrid ? "Main String Tension" : "Main Tension"}
                            </label>
                            <div className={styles.tensionInputRow}>
                              <input
                                type="number"
                                min="1"
                                step="0.5"
                                value={tensionMain}
                                onChange={(e) => setTensionMain(e.target.value)}
                                className={styles.tensionInput}
                                placeholder="e.g. 55"
                              />
                              <span className={styles.tensionUnitLabel}>{tensionUnit}</span>
                            </div>
                          </div>
                        ) : (
                          <div className={styles.tensionField}>
                            <label className={styles.tensionFieldLabel}>
                              Cross String Tension
                            </label>
                            <div className={styles.tensionInputRow}>
                              <input
                                type="number"
                                min="1"
                                step="0.5"
                                value={tensionCross}
                                onChange={(e) => setTensionCross(e.target.value)}
                                className={styles.tensionInput}
                                placeholder="e.g. 53"
                              />
                              <span className={styles.tensionUnitLabel}>{tensionUnit}</span>
                            </div>
                          </div>
                        )}
                      </div>

                      {isHybrid ? (
                        tensionModalTarget !== "cross" ? (
                          <div className={styles.prestretchRow}>
                            <label
                              className={`${styles.prestretchLabel} ${
                                !prestretchEnabled ? styles.prestretchLabelDisabled : ""
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={prestretchEnabled}
                                onChange={(e) => {
                                  setPrestretchEnabled(e.target.checked);
                                  if (!e.target.checked) setPrestretchPercent("");
                                }}
                                className={styles.prestretchCheckbox}
                              />
                              Main Prestretch
                            </label>
                            <div className={styles.tensionInputRow}>
                              <input
                                type="number"
                                min="0"
                                max="100"
                                step="0.5"
                                value={prestretchPercent}
                                onChange={(e) => setPrestretchPercent(e.target.value)}
                                disabled={!prestretchEnabled}
                                className={`${styles.tensionInput} ${
                                  !prestretchEnabled ? styles.tensionInputDisabled : ""
                                }`}
                                placeholder="e.g. 10"
                              />
                              <span
                                className={`${styles.tensionUnitLabel} ${
                                  !prestretchEnabled ? styles.tensionUnitDisabled : ""
                                }`}
                              >
                                %
                              </span>
                            </div>
                          </div>
                        ) : (
                          <div className={styles.prestretchRow}>
                            <label
                              className={`${styles.prestretchLabel} ${
                                !prestretchEnabledCross ? styles.prestretchLabelDisabled : ""
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={prestretchEnabledCross}
                                onChange={(e) => {
                                  setPrestretchEnabledCross(e.target.checked);
                                  if (!e.target.checked) setPrestretchPercentCross("");
                                }}
                                className={styles.prestretchCheckbox}
                              />
                              Cross Prestretch
                            </label>
                            <div className={styles.tensionInputRow}>
                              <input
                                type="number"
                                min="0"
                                max="100"
                                step="0.5"
                                value={prestretchPercentCross}
                                onChange={(e) => setPrestretchPercentCross(e.target.value)}
                                disabled={!prestretchEnabledCross}
                                className={`${styles.tensionInput} ${
                                  !prestretchEnabledCross ? styles.tensionInputDisabled : ""
                                }`}
                                placeholder="e.g. 10"
                              />
                              <span
                                className={`${styles.tensionUnitLabel} ${
                                  !prestretchEnabledCross ? styles.tensionUnitDisabled : ""
                                }`}
                              >
                                %
                              </span>
                            </div>
                          </div>
                        )
                      ) : (
                        <div className={styles.prestretchRow}>
                          <label
                            className={`${styles.prestretchLabel} ${
                              !prestretchEnabled ? styles.prestretchLabelDisabled : ""
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={prestretchEnabled}
                              onChange={(e) => {
                                setPrestretchEnabled(e.target.checked);
                                if (!e.target.checked) setPrestretchPercent("");
                              }}
                              className={styles.prestretchCheckbox}
                            />
                            Prestretch
                          </label>
                          <div className={styles.tensionInputRow}>
                            <input
                              type="number"
                              min="0"
                              max="100"
                              step="0.5"
                              value={prestretchPercent}
                              onChange={(e) => setPrestretchPercent(e.target.value)}
                              disabled={!prestretchEnabled}
                              className={`${styles.tensionInput} ${
                                !prestretchEnabled ? styles.tensionInputDisabled : ""
                              }`}
                              placeholder="e.g. 10"
                            />
                            <span
                              className={`${styles.tensionUnitLabel} ${
                                !prestretchEnabled ? styles.tensionUnitDisabled : ""
                              }`}
                            >
                              %
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  <div style={{ display: "flex", gap: "10px", marginTop: "20px" }}>
                    {isHybrid && tensionModalTarget !== "cross" && selectedStringIdCross && (
                      <button
                        type="button"
                        className={styles.closeModalButton}
                        style={{ background: "#f8fafc", color: "#0b7b43", border: "1px solid #0b7b43" }}
                        onClick={() => setTensionModalTarget("cross")}
                      >
                        Next: Cross Tension →
                      </button>
                    )}
                    <button
                      type="button"
                      className={styles.closeModalButton}
                      onClick={() => setIsTensionModalOpen(false)}
                    >
                      {isHybrid && tensionModalTarget !== "cross" && !selectedStringIdCross
                        ? "Save Main Tension"
                        : "Done / Close"}
                    </button>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
};

export default BookingForm;
